<template>
  <omint-page>
    <Header
      name="Documento"
      :titleLabel="doc.name"
    />
    <omint-page-content>
      <omt-highlight v-if="loading">Carregando {{ doc.name }}. Por favor, aguarde.</omt-highlight>
      <omt-highlight v-else-if="error">{{ error }}</omt-highlight>

      <div
        v-if="usesPdfJs"
        v-show="!loading && !error"
        ref="pdfViewer"
        class="pdf-viewer"
        :aria-label="doc.name"
      />
      <iframe
        v-else
        id="iframe-document"
        :title="doc.name"
        :src="doc.url"
        allowfullscreen
        :width="width"
        :height="height"
        @load="fileLoaded"
      />

      <div
        v-if="canDownloadNative"
        class="download-action"
      >
        <omt-button :onClick="downloadFile">Baixar ou compartilhar</omt-button>
      </div>
    </omint-page-content>
    <Footer />
  </omint-page>
</template>

<script>
import Header from "@/components/Molecules/Header";
import Footer from "@/components/Molecules/Footer";
import { isDataUri } from "@/utils/downloadUtil";
import { mapGetters, mapState } from "vuex";

let pdfjsPromise;

function loadPdfJs() {
  if (!pdfjsPromise) {
    pdfjsPromise = Promise.all([
      import(/* webpackChunkName: "pdf-viewer" */ "pdfjs-dist/legacy/build/pdf"),
      import(/* webpackChunkName: "pdf-worker" */ "worker-loader!pdfjs-dist/legacy/build/pdf.worker")
    ]).then(([pdfjsModule, workerModule]) => {
      const pdfjs = pdfjsModule.default || pdfjsModule;
      const PdfWorker = workerModule.default || workerModule;
      pdfjs.GlobalWorkerOptions.workerPort = new PdfWorker();
      return pdfjs;
    });
  }

  return pdfjsPromise;
}

export default {
  name: "DisplayFile",
  components: { Header, Footer },
  data() {
    return {
      loading: true,
      error: "",
      pdfDocument: null,
      renderVersion: 0,
      resizeTimer: null,
      width: 0,
      height: 0
    };
  },
  computed: {
    ...mapState("myFiles", ["doc"]),
    ...mapGetters("biometrics", ["onNative"]),
    usesPdfJs() {
      return typeof this.doc?.url === "string" && this.doc.url.startsWith("data:application/pdf");
    },
    canDownloadNative() {
      return this.onNative && this.usesPdfJs;
    }
  },
  mounted() {
    if (!this.doc || !this.doc.name || !this.doc.url) {
      this.$router.back();
      return;
    }

    globalThis.window.addEventListener("resize", this.scheduleRender);
    this.onResizeHandler();
    if (this.usesPdfJs) {
      this.loadDocument();
    }
  },
  beforeDestroy() {
    this.renderVersion++;
    globalThis.window.removeEventListener("resize", this.scheduleRender);
    globalThis.window.clearTimeout(this.resizeTimer);
    this.pdfDocument?.destroy();
  },
  methods: {
    pdfSource(url) {
      if (!isDataUri(url)) return url;

      const separator = url.indexOf(",");
      if (separator < 0) throw new Error("Data URI do documento invalido");

      const metadata = url.slice(0, separator);
      const content = url.slice(separator + 1);
      const binary = metadata.includes(";base64") ? atob(content) : decodeURIComponent(content);
      const bytes = new Uint8Array(binary.length);

      for (let index = 0; index < binary.length; index++) {
        bytes[index] = binary.charCodeAt(index);
      }

      return { data: bytes };
    },
    async loadDocument() {
      try {
        this.loading = true;
        this.error = "";

        const pdfjs = await loadPdfJs();
        const loadingTask = pdfjs.getDocument(this.pdfSource(this.doc.url));
        this.pdfDocument = await loadingTask.promise;
        this.loading = false;
        await this.$nextTick();
        await this.renderPages();
      } catch (error) {
        console.error("Erro ao exibir PDF", error);
        this.error = "Não foi possível exibir este documento.";
      } finally {
        this.loading = false;
      }
    },
    async renderPages() {
      const viewer = this.$refs.pdfViewer;
      if (!viewer || !this.pdfDocument) return;

      const currentVersion = ++this.renderVersion;
      viewer.innerHTML = "";

      for (let pageNumber = 1; pageNumber <= this.pdfDocument.numPages; pageNumber++) {
        if (currentVersion !== this.renderVersion) return;

        const page = await this.pdfDocument.getPage(pageNumber);
        const baseViewport = page.getViewport({ scale: 1 });
        const availableWidth = Math.min(viewer.clientWidth || globalThis.window.innerWidth - 32, 970);
        const viewport = page.getViewport({ scale: availableWidth / baseViewport.width });
        const outputScale = Math.min(globalThis.window.devicePixelRatio || 1, 2);
        const pageElement = document.createElement("div");
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");

        if (!context) throw new Error("Canvas indisponivel");

        pageElement.className = "pdf-page";
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        canvas.setAttribute("aria-label", `Página ${pageNumber} de ${this.pdfDocument.numPages}`);
        pageElement.appendChild(canvas);
        viewer.appendChild(pageElement);

        await page.render({
          canvasContext: context,
          viewport,
          transform: outputScale === 1 ? null : [outputScale, 0, 0, outputScale, 0, 0]
        }).promise;
      }
    },
    scheduleRender() {
      this.onResizeHandler();
      if (!this.usesPdfJs) return;

      globalThis.window.clearTimeout(this.resizeTimer);
      this.resizeTimer = globalThis.window.setTimeout(() => this.renderPages(), 150);
    },
    fileLoaded() {
      this.loading = false;
      this.$root.$emit("hide-loading");
    },
    onResizeHandler() {
      const borders = 25 * 2;
      const headerFooter = borders + 55 * 2;
      const maxWidth = 970;
      this.width = Math.min(globalThis.window.innerWidth - borders, maxWidth);
      this.height = globalThis.window.innerHeight - headerFooter;
    },
    async downloadFile() {
      if (!this.canDownloadNative) return;

      const url = this.doc.url;
      const mimeType = url.match(/^data:([^;,]+)/i)?.[1] || "application/pdf";
      const fileName = /\.pdf$/i.test(this.doc.name || "") ? this.doc.name : `${this.doc.name || "documento"}.pdf`;

      await this.$native.postMessage(
        JSON.stringify({
          type: "download_blob",
          payload: {
            fileName,
            mimeType,
            data: url
          }
        })
      );
    }
  }
};
</script>

<style scoped>
.pdf-viewer {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.pdf-viewer ::v-deep .pdf-page {
  background: #ffffff;
  box-shadow: 0 2px 10px rgba(16, 42, 69, 0.16);
  line-height: 0;
  max-width: 970px;
}

.pdf-viewer ::v-deep canvas {
  height: auto !important;
  max-width: 100%;
}

.download-action {
  display: flex;
  justify-content: center;
  margin: 24px 0;
}
</style>
